import '@fontsource/roboto';
import '@fontsource/roboto/400-italic.css';
import '@fontsource/roboto/400.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faIgloo } from '@fortawesome/free-solid-svg-icons';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { grey } from '@mui/material/colors';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { RouterStore } from '@superwf/mobx-react-router';
import axios, { isCancel } from 'axios';
import { createBrowserHistory } from 'history';
import { action, configure, makeObservable, reaction, runInAction, when } from 'mobx';
import { Provider, observer } from 'mobx-react';
import moment from 'moment';
import brLocale from 'moment/dist/locale/pt-br';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Routes } from 'react-router';
import { Route, Router } from 'react-router-dom';
import './App.css';
import Calendario from './components/Calendario';
import ErrorBoundary from './components/ErrorBoundary';
import FooterApp from './components/FooterApp';
import MenuAppBar from './components/MenuAppBar';
import ParametrosSistema from './components/ParametrosSistema';
import TelaErro from './components/TelaErro';
import TelaPrincipal from './components/TelaPrincipal';
import TelaVersao from './components/TelaVersao';
import AcompanhamentoSessao from './components/sessao/AcompanhamentoSessao';
import PautaJulgamento from './components/sessao/PautaJulgamento';
import PreSessao from './components/sessao/PreSessao';
import SalaSessao from './components/sessao/SalaSessao';
import Rota, { ROTA } from './dominio/Rota';
import ApmUser from './entities/ApmUser';
import CertidaoService from './service/CertidaoService';
import RootStore from './store/RootStore';
import SalaSessaoStore from './store/SalaSessaoStore';
import Ambiente from './util/Ambiente';
import Configuracao from './util/Configuracao';
import DOMUtil from './util/DOMUtil';
import HotjarUtil from './util/HotjarUtil';
import SentryUtil from './util/SentryUtil';
import StorageUtil from './util/StorageUtil';

// Configuração da biblioteca de ícones gratuitos do Font Awesome
library.add(faIgloo);

const history = createBrowserHistory();
const routingStore = new RouterStore(history);
const rootStore = new RootStore(routingStore);

class App extends Component {
    static NOME_STORAGE_VERSAO_ATUAL = StorageUtil.PREFIXO_NOME_STORAGE + 'versao-atual-frontend';
    static NOME_STORAGE_VERSAO_ANTIGA = StorageUtil.PREFIXO_NOME_STORAGE + 'versao-antiga-frontend';
    static CAMINHO_RELATIVO_SCRIPT_LOGUS = '/js/login-client.js';

    disposers = [];
    loginClient;
    inicializandoApp = true;
    scriptLogusAdicionado = false;
    baseUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL : '';

    constructor(props) {
        super(props);

        makeObservable(this, {
            disposeReactions: action,
            updateWindowDimensions: action,
            inicializarApp: action,
            abrirApp: action,
            fecharApp: action,
            criarRenovarToken: action
        });

        console.log('Iniciando App#constructor...');
        moment.locale('pt-br');
        this.configurarMobx();
        this.configurarAxios();
        this.inicializarApp(); // TODO Refactor this asynchronous operation outside of the constructor.sonarqube(javascript:S7059)
        this.configurarReactions();
    }

    configurarReactions() {
        this.disposers.push(
            reaction(
                () => rootStore.appStore.carregarApp,
                (carregarApp) => {
                    if (!carregarApp) {
                        rootStore.limparStores();
                        this.logusLogout();
                    }
                }
            )
        );

        this.disposers.push(
            reaction(
                () => rootStore.mensagemStore.mensagem,
                (mensagem) => {
                    if (mensagem) {
                        enqueueSnackbar(mensagem.conteudo, {
                            variant: rootStore.mensagemStore.mensagem.tipo,
                            autoHideDuration: rootStore.mensagemStore.mensagem.duracao,
                            preventDuplicate: rootStore.mensagemStore.mensagem.evitarDuplicacao,
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'center'
                            }
                        });

                        setTimeout(() => {
                            runInAction(() => {
                                rootStore.mensagemStore.exibir = false;
                            });
                        }, rootStore.mensagemStore.mensagem.duracao || Configuracao.TEMPO_PADRAO_EXIBICAO_MENSAGEM);
                    }
                }
            )
        );

        this.disposers.push(
            reaction(
                () => rootStore.appStore.conectividadeOK,
                () => {
                    if (!rootStore.appStore.conectividadeOK) {
                        rootStore.mensagemStore.abrirMensagemErro(
                            `Não foi possível estabelecer uma conexão com os servidores da Secretaria Eletrônica. Verifique as conexões de rede ou solicite suporte técnico. Sistema operando em modo off-line. Não é possível apregoar processos, carregar ou gravar informações.`
                        );
                    } else if (typeof this.props?.closeSnackbar !== 'undefined') {
                        this.props.closeSnackbar();
                    }
                }
            )
        );
    }

    /**
     * @see {@link https://mobx.js.org/configuration.html}
     */
    configurarMobx() {
        configure({
            enforceActions: 'always',
            computedRequiresReaction: true,
            reactionRequiresObservable: true,
            observableRequiresReaction: false,
            disableErrorBoundaries: false
        });

        const REFATORANDO = false;
        const DEBUGANDO = false;

        if (DEBUGANDO) {
            // To make sure MobX is as strict as possible
            configure({
                disableErrorBoundaries: true,
                reactionScheduler: (f) => {
                    const tempoMS = 100;
                    console.log(`Aguardando ${tempoMS}ms para executar a reaction`, f);
                    setTimeout(f, tempoMS);
                }
            });
        } else if (REFATORANDO) {
            configure({
                observableRequiresReaction: true
            });
        }
    }

    configurarAxios() {
        axios.defaults.timeout = Configuracao.DEFAULT_AXIOS_TIMEOUT;
        axios.defaults.headers.common['Accept'] = '*/*';
        axios.interceptors.request.use(this.requestInterceptorHandler, this.requestInterceptorErrorHandler);
        axios.interceptors.response.use(this.responseInterceptorHandler, this.responseInterceptorErrorHandler);
    }

    requestInterceptorHandler = (config) => {
        if (!config.background) {
            rootStore.appStore.incrementarRequisicoes();
        }
        return config;
    };

    requestInterceptorErrorHandler = (error) => {
        rootStore.errorStore.registrarErro(error);
        rootStore.logStore.registrarErro({ resposta: JSON.stringify(error) });
        rootStore.appStore.zerarRequisicoes();
        return Promise.reject(error);
    };

    responseInterceptorHandler = (response) => {
        if (!response.config || (response.config && !response.config.background)) {
            rootStore.appStore.decrementarRequisicoes();
        }
        if (response.config) {
            Ambiente.removerRequisicao(response.config.url);
        }
        if (!this.isTratamentoErroComponente(response)) {
            this.tratarMensagensErroStatus200(response);
        }
        return response;
    };

    tratarMensagensErroStatus200(response) {
        let mensagemRetorno;

        if (Array.isArray(response.data)) {
            let erros = response.data.filter((erro) => erro && erro.tipo === 'ERRO');
            let mensagens = erros.map((erro) => erro.mensagens || erro.mensagem).filter(Boolean);
            mensagemRetorno = mensagens.length > 0 ? mensagens : undefined;
            response.data['erro'] = mensagemRetorno !== undefined;
        } else if (response?.data?.tipo === 'ERRO') {
            mensagemRetorno = response.data.mensagens || response.data.mensagem;
            response.data.erro = mensagemRetorno !== undefined;
        }

        if (mensagemRetorno) {
            rootStore.logStore.registrarErro({ status: response.status, resposta: JSON.stringify(response.data) });
            rootStore.mensagemStore.abrirMensagemErro(mensagemRetorno);
        }
    }

    responseInterceptorErrorHandler = (error) => {
        if (!isCancel(error)) {
            rootStore.appStore.zerarRequisicoes();

            if (!this.isTratamentoErroComponente(error)) {
                rootStore.errorStore.tratarRespostaErro(error, this.logusLogout);
            }
            return Promise.reject(error);
        }
        return false;
    };

    isTratamentoErroComponente(error) {
        return error?.config?.tratamentoErroComponente;
    }

    componentDidMount() {
        rootStore.appStore.theme = this.props.theme;
        window.addEventListener('resize', this.updateWindowDimensions);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
        rootStore.disposeStoresReactions();
        this.disposeReactions();

        // @ts-ignore
        if (rootStore.appStore?.verificadorConectividade) {
            // @ts-ignore
            clearInterval(rootStore.appStore.verificadorConectividade);
        }
    }

    disposeReactions() {
        this.disposers.forEach((disposer) => {
            if (disposer) {
                disposer();
            }
        });
        this.disposers = [];
    }

    updateWindowDimensions = () => {
        rootStore.appStore.larguraJanela = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
        rootStore.appStore.alturaJanela = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    };

    /**
     * Verifica se é o primeiro carregamento de uma versão mais recente. Se for, recarrega.
     */
    verificarVersao() {
        const versaoAntiga = localStorage.getItem(App.NOME_STORAGE_VERSAO_ANTIGA);
        const versaoAtual = localStorage.getItem(App.NOME_STORAGE_VERSAO_ATUAL);

        if (versaoAntiga !== null && versaoAntiga !== versaoAtual) {
            console.info(
                `Identificada nova versão do frontend. [VERSAO ANTIGA: ${versaoAntiga} | VERSAO ATUAL: ${versaoAtual}]`
            );

            if (versaoAtual !== null) {
                localStorage.setItem(App.NOME_STORAGE_VERSAO_ANTIGA, versaoAtual);
            }

            // Recarrega a aplicação
            console.log('Recarregando rota corrente...');
            window.document.location.reload(); // [REVIEW]
        }
    }

    async inicializarApp() {
        rootStore.appStore.inicializar();
        rootStore.appStore.larguraJanela = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
        rootStore.appStore.alturaJanela = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

        if (rootStore.appStore.tipoNavegacaoEntradaApp !== 'reload') {
            rootStore.usuarioStore.usuario = undefined;
        }

        await Ambiente.inicializar();

        this.baseUrl =
            Ambiente.baseUrl?.contexto !== undefined && Ambiente.baseUrl?.contexto !== ''
                ? `/${Ambiente.baseUrl?.contexto}`
                : process.env.PUBLIC_URL
                  ? process.env.PUBLIC_URL
                  : '';

        // "Zera" as dependências da aplicação se for publicada uma nova versão do frontend
        localStorage.setItem(App.NOME_STORAGE_VERSAO_ATUAL, Ambiente.getBuildInfo().buildAplicacao);
        this.verificarVersao();

        // Exclui históricos de rascunhos de certidões antigos
        const totalHistoricosRascunhoCertidaoExcluidos = CertidaoService.excluirHistoricosRascunhoCertidao();

        if (totalHistoricosRascunhoCertidaoExcluidos > 0) {
            console.log(
                `Total de históricos de rascunhos de certidões antigos excluídos: ${totalHistoricosRascunhoCertidaoExcluidos}.`
            );
        }

        // Exclui cache de minutas de votos acessadas há muito tempo
        SalaSessaoStore.excluirMinutasVotoDoCache().then(
            (totalMinutasVotoExcluidasDoCache) => {
                console.log(`Total de minutas de votos excluídas do cache: ${totalMinutasVotoExcluidasDoCache}.`);
            },
            (reason) => {
                console.error('Falha ao limpar o cache de minutas de votos.', reason);
            }
        );

        await rootStore.inicializarStoresSistema();

        if (window.location.pathname === this.baseUrl + ROTA.TELA_ERRO.rota) {
            this.abrirApp();
        } else if (window.location.pathname === this.baseUrl + ROTA.ACOMPANHAMENTO_SESSAO.rota) {
            this.abrirApp();
        } else if (window.location.pathname === this.baseUrl + ROTA.TELA_VERSAO.rota) {
            this.abrirApp();
        } else {
            this.iniciarLoginLogus();
        }
    }

    static definirApmUserInfo() {
        if (rootStore?.usuarioStore?.usuario) {
            const apmUser = new ApmUser();
            apmUser.id = rootStore.usuarioStore.usuario.cpf ? rootStore.usuarioStore.usuario.cpf : undefined;
            apmUser.localizacao = rootStore.usuarioStore.usuario.localizacao
                ? rootStore.usuarioStore.usuario.localizacao
                : undefined;
            apmUser.username = rootStore.usuarioStore.usuario.codigo
                ? rootStore.usuarioStore.usuario.codigo
                : undefined;
            apmUser.nome = rootStore.usuarioStore.usuario.nome ? rootStore.usuarioStore.usuario.nome : undefined;
            SentryUtil.setSentryUser(apmUser);
            HotjarUtil.setUser(apmUser);
        }
    }

    verificarScriptLogusDisponivel() {
        return (
            this.scriptLogusAdicionado ||
            DOMUtil.verificarScriptIncluido(Ambiente.urls.urlLogus + App.CAMINHO_RELATIVO_SCRIPT_LOGUS)
        );
    }

    abrirApp = () => {
        rootStore.appStore.abrirApp();
        rootStore.routerStore.push(this.proximaRota);
    };

    get proximaRota() {
        let route = window.location.pathname;

        if (this.validarRota(route)) {
            return route;
        }

        let lastRoute = rootStore.routerStore.lastRoute;

        if (this.validarRota(lastRoute)) {
            return lastRoute;
        }

        return this.baseUrl + ROTA.CALENDARIO.rota;
    }

    fecharApp = () => {
        rootStore.appStore.fecharApp();
        rootStore.routerStore.push(this.baseUrl + '/');
    };

    logusLogout = () => {
        if (this.loginClient) {
            this.loginClient.logout();
        }
    };

    iniciarLoginLogus() {
        if (!Ambiente.urls) {
            throw new Error('Falha ao recuperar as configurações de ambiente.');
        }

        if (this.verificarScriptLogusDisponivel()) {
            console.info('LogUs já inicializado.');
            return;
        }

        let script = document.createElement('script');

        try {
            script.src = Ambiente.urls.urlLogus + App.CAMINHO_RELATIVO_SCRIPT_LOGUS;
            script.onerror = this.onLoginError;
            script.onload = this.onLoadApp;
            document.body.appendChild(script);
            this.scriptLogusAdicionado = true;
        } catch (e) {
            console.error(e);
        }
    }

    onLoginError = () => {
        rootStore.mensagemStore.abrirMensagemErro('Falha ao carregar a tela de autenticação.');
    };

    onLoadApp = async () => {
        this.loginClient = await this.construirLoginClient();
        await this.loginClient.inicializar();
    };

    async construirLoginClient() {
        const response = await fetch(Ambiente.urls.urlLogus + '/configs', { headers: { accept: 'application/json' } });
        const urlPaginaAutenticacao = await response.json();
        const settings = {
            urlPaginaAutenticacao: urlPaginaAutenticacao.urlLogin,
            urlLoginServer: Ambiente.urls.urlLogus,
            nomeAplicacaoOrigem: Ambiente.getBuildInfo().nomeAplicacao,
            versaoAplicacaoOrigem: `${Ambiente.getBuildInfo().numeroVersao} de ${Ambiente.getBuildInfo().dataVersao}`,
            cbAlteracaoToken: this.criarRenovarToken,
            cbErroAlteracaoToken: (erro) => {
                console.error(erro);
                this.fecharApp();
            }
        };

        // @ts-ignore
        return window.novoLoginClient(settings);
    }

    criarRenovarToken = async (token, statusErro) => {
        if (!token) {
            if (statusErro) {
                rootStore.mensagemStore.abrirMensagemErro(
                    'Autenticação perdida: atualize a tela ou aguarde para efetuar novo login.'
                );
                when(
                    () => rootStore.mensagemStore.exibir === false,
                    () => window.location.reload()
                );
            } else {
                console.log('reload em criarRenovarToken');
                window.location.reload();
            }
        } else if (rootStore.usuarioStore.usuario) {
            /** @see {@link App#inicializarApp} */
            rootStore.usuarioStore.usuario.token = token;
            App.definirApmUserInfo();
        } else {
            await this.configurarUsuario(token);
        }
    };

    async configurarUsuario(token) {
        let perfilOK = await rootStore.usuarioStore.configurarUsuario(token);

        if (perfilOK) {
            await this.inicializarStoresUsuario();
            App.definirApmUserInfo();
            this.abrirApp();
        } else {
            rootStore.mensagemStore.abrirMensagemAlerta(
                'O perfil selecionado não tem autorização para acessar a Secretaria Eletrônica.'
            );
            when(
                () => rootStore.mensagemStore.exibir === false,
                () => this.logusLogout()
            );
        }
    }

    // Inicializa as stores de usuário apenas na inicialização do sistema, evitando que seja feito na renovação do token
    async inicializarStoresUsuario() {
        if (this.inicializandoApp) {
            await rootStore.inicializarStoresUsuario();
            this.inicializandoApp = false;
        }
    }

    validarRota(nomeRota) {
        const nomeRotaLimpa = nomeRota?.substring(nomeRota.lastIndexOf('/'));
        const idRota = Rota.getIdByRota(nomeRotaLimpa);
        return !!idRota;
    }

    render() {
        return (
            <Provider stores={rootStore}>
                {rootStore.appStore.carregarApp ? this.renderApp() : this.renderCarregando()}
            </Provider>
        );
    }

    renderCarregando() {
        return (
            <>
                <div style={{ display: 'flex', alignItems: 'center', margin: '20px' }}>
                    <CircularProgress size={25} />
                    <Typography
                        color={grey[500]}
                        variant="h6"
                        sx={{ margin: '25px 10px', fontStyle: 'italic' }}
                    >
                        Carregando...
                    </Typography>
                </div>
                <div style={{ display: 'flex', flexGrow: 1 }} />
            </>
        );
    }

    renderApp() {
        const pathname = rootStore.routerStore.location?.pathname;
        return (
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={'pt-br'}
                localeText={brLocale}
            >
                <ErrorBoundary>
                    {window.location.pathname === this.baseUrl + ROTA.TELA_VERSAO.rota ? (
                        <Router
                            basename={this.baseUrl}
                            navigator={history}
                            location={history.location}
                        >
                            <Routes>
                                <Route
                                    path={ROTA.TELA_VERSAO.rota}
                                    element={<TelaVersao />}
                                />
                            </Routes>
                        </Router>
                    ) : (
                        <>
                            <Backdrop
                                sx={{ color: '#fff', zIndex: 99999999 }}
                                open={rootStore.appStore.backdropOpen}
                            >
                                <CircularProgress color="inherit" />
                            </Backdrop>
                            <MenuAppBar />
                            {rootStore.routerStore.redirecionandoRota ? (
                                this.renderCarregando()
                            ) : (
                                <Paper
                                    style={{ display: 'flex', flexGrow: 1 }}
                                    elevation={0}
                                >
                                    <Router
                                        basename={this.baseUrl}
                                        navigator={history}
                                        location={history.location}
                                    >
                                        <Routes key={pathname}>
                                            {/* Novas rotas devem ser adicionadas ao objeto de domínio "ROTA" */}
                                            <Route
                                                path={ROTA.MENU.rota}
                                                element={<TelaPrincipal />}
                                            />
                                            <Route
                                                path={ROTA.CALENDARIO.rota}
                                                element={<Calendario />}
                                            />
                                            <Route
                                                path={ROTA.SESSAO.rota}
                                                element={<SalaSessao />}
                                            />
                                            <Route
                                                path={ROTA.PAUTA.rota}
                                                element={<PautaJulgamento />}
                                            />
                                            <Route
                                                path={ROTA.ACOMPANHAMENTO_SESSAO.rota}
                                                element={<AcompanhamentoSessao />}
                                            />
                                            <Route
                                                path={ROTA.ADMIN.rota}
                                                element={<ParametrosSistema />}
                                            />
                                            <Route
                                                path={ROTA.PRE_SESSAO.rota}
                                                element={<PreSessao />}
                                            />
                                            <Route
                                                path={ROTA.TELA_ERRO.rota}
                                                element={<TelaErro />}
                                            />
                                            <Route
                                                path={ROTA.TELA_VERSAO.rota}
                                                element={<TelaVersao />}
                                            />
                                        </Routes>
                                    </Router>
                                </Paper>
                            )}
                            <FooterApp />
                        </>
                    )}
                </ErrorBoundary>
            </LocalizationProvider>
        );
    }
}

App.propTypes = {
    theme: PropTypes.object.isRequired,
    closeSnackbar: PropTypes.func
};

export default observer(App);
